import React, { useState, useEffect, useContext } from 'react';
import {
	Box,
	Container,
	Paper,
	TextField,
	Button,
	Typography,
	Alert,
	CircularProgress,
	InputAdornment,
	IconButton,
	Stack,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import * as http from '../../base-ui/utils/http';
import { ConfigContext } from '../../base-ui/Config/ConfigContext';
import auth from '../../base-ui/utils/auth';

export function SetPassword() {
	const [token, setToken] = useState<string>('');
	const [password, setPassword] = useState<string>('');
	const [confirmPassword, setConfirmPassword] = useState<string>('');
	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string>('');
	const [success, setSuccess] = useState<boolean>(false);
	const navigate = useNavigate();
	const { config }: any = useContext(ConfigContext);

	useEffect(() => {
		// Extract token from URL query params
		const queryParams = new URLSearchParams(window.location.search);
		const magicLinkToken = queryParams.get('token');

		if (!magicLinkToken) {
			setError('Invalid or missing magic link. Please contact support or request a new invitation.');
		} else {
			setToken(magicLinkToken);
		}
	}, []);

	const validatePassword = (pwd: string): string | null => {
		if (pwd.length < 8) {
			return 'Password must be at least 8 characters long';
		}
		if (pwd.includes('&')) {
			return 'Password cannot contain "&" character';
		}
		if (!/(?=.*[a-z])/.test(pwd)) {
			return 'Password must contain at least one lowercase letter';
		}
		if (!/(?=.*[A-Z])/.test(pwd)) {
			return 'Password must contain at least one uppercase letter';
		}
		if (!/(?=.*\d)/.test(pwd)) {
			return 'Password must contain at least one number';
		}
		return null;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError('');

		// Validation
		if (!password || !confirmPassword) {
			setError('Please fill in all fields');
			return;
		}

		if (password !== confirmPassword) {
			setError('Passwords do not match');
			return;
		}

		const validationError = validatePassword(password);
		if (validationError) {
			setError(validationError);
			return;
		}

		setLoading(true);

		try {
			const response = await http.post(
				`${config.uri.base}/password/set`,
				{
					password: password,
				},
				{
					authHeader: false,
					headers: {
						'Authorization': `Bearer ${token}`,
						'x-app-id': process.env.REACT_APP_APPID
					}
				}
			);

			if (response.errored) {
				setError(response.error?.message || 'Failed to set password. Please try again.');
				setLoading(false);
			} else {
				setSuccess(true);
				setLoading(false);

				// Redirect to Cognito login after 2 seconds so user can complete authentication
				setTimeout(() => {
					window.location.href = config.loginUrl;
				}, 2000);
			}
		} catch (err: any) {
			setError(err.message || 'An unexpected error occurred. Please try again.');
			setLoading(false);
		}
	};

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const handleClickShowConfirmPassword = () => {
		setShowConfirmPassword(!showConfirmPassword);
	};

	if (!token && error) {
		return (
			<Box
				sx={{
					minHeight: '100vh',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					backgroundColor: 'background.default',
				}}
			>
				<Box sx={{ width: '100%', maxWidth: 420, px: 2 }}>
					<Paper sx={{ p: 4 }}>
						<Stack spacing={3} alignItems="center">
							<Typography variant="h2" component="h1" align="center" color="secondary.800">
								Invalid Link
							</Typography>
							<Alert severity="error" sx={{ width: '100%' }}>
								{error}
							</Alert>
							<Button
								fullWidth
								variant="contained"
								color="primary"
								onClick={() => navigate('/login')}
							>
								Go to Login
							</Button>
						</Stack>
					</Paper>
				</Box>
			</Box>
		);
	}

	if (success) {
		return (
			<Box
				sx={{
					minHeight: '100vh',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					backgroundColor: 'background.default',
				}}
			>
				<Box sx={{ width: '100%', maxWidth: 420, px: 2 }}>
					<Paper sx={{ p: 4 }}>
						<Stack spacing={3} alignItems="center">
							<Typography variant="h2" component="h1" align="center" color="secondary.800">
								Password Set Successfully!
							</Typography>
							<Alert severity="success" sx={{ width: '100%' }}>
								Your password has been set. Redirecting you to login...
							</Alert>
							<CircularProgress color="primary" />
						</Stack>
					</Paper>
				</Box>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				minHeight: '100vh',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				backgroundColor: 'background.default',
			}}
		>
			<Box sx={{ width: '100%', maxWidth: 420, px: 2 }}>
				<Paper sx={{ p: 4 }}>
					<Stack spacing={3}>
						<Box sx={{ textAlign: 'center' }}>
							<img
								src="/deck_metrix_logo.svg"
								alt="DeckMetriX"
								style={{ height: 40, marginBottom: 16 }}
							/>
							<Typography variant="h2" component="h1" gutterBottom color="secondary.800">
								Set Your Password
							</Typography>
							<Typography variant="sm" color="secondary.600">
								Create a password to complete your registration
							</Typography>
						</Box>

						{error && (
							<Alert severity="error">
								{error}
							</Alert>
						)}

						<form onSubmit={handleSubmit}>
							<Stack spacing={2.5}>
								<TextField
									fullWidth
									label="New Password"
									type={showPassword ? 'text' : 'password'}
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									disabled={loading}
									required
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													onClick={handleClickShowPassword}
													edge="end"
													disabled={loading}
												>
													{showPassword ? <VisibilityOff /> : <Visibility />}
												</IconButton>
											</InputAdornment>
										),
									}}
									helperText="Must be at least 8 characters with uppercase, lowercase, and numbers"
								/>

								<TextField
									fullWidth
									label="Confirm Password"
									type={showConfirmPassword ? 'text' : 'password'}
									value={confirmPassword}
									onChange={(e) => setConfirmPassword(e.target.value)}
									disabled={loading}
									required
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													onClick={handleClickShowConfirmPassword}
													edge="end"
													disabled={loading}
												>
													{showConfirmPassword ? <VisibilityOff /> : <Visibility />}
												</IconButton>
											</InputAdornment>
										),
									}}
								/>

								<Button
									type="submit"
									fullWidth
									variant="contained"
									color="primary"
									disabled={loading}
									sx={{
										py: 1.5,
										fontSize: 'md',
										fontWeight: 600,
									}}
								>
									{loading ? (
										<CircularProgress size={24} sx={{ color: 'white' }} />
									) : (
										'Set Password & Get Started'
									)}
								</Button>
							</Stack>
						</form>
					</Stack>
				</Paper>
			</Box>
		</Box>
	);
}
